import type { FiscalWorkstationStorage } from '@/entities/fiscalization';
import type { KkmExecutionResult, KkmServerAdapter } from '@/shared/api';

import { classifyCardCancel, mapCardCancelCommand } from '@/entities/fiscalization';
import {
  type CancelOrderFiscalPaymentInput,
  type OrderFieldsFragment,
  type OrderFiscalExecutionFieldsFragment,
  OrderFiscalFailureKind,
  OrderFiscalRecoveryAction,
  OrderStatus,
} from '@shared/api/graphql/gql/graphql';

import type { OrderFiscalizationPhase } from './order-fiscalization-state';
import type { OrderFiscalizationServices } from './order-fiscalization-types';

import { executeKkmSafely } from './execute-kkm-safely';
import { notifyKkmVendorNotice } from './kkm-vendor-notice';
import {
  BANK_REFERENCE_FIELDS,
  hasBankReferences,
  toBankEvidenceInput,
  mergeAcquiring,
  isReceiptRetryAllowed,
  acquiringCommandId,
  requireReadyHandshake,
  assertExecutionTarget,
  safeErrorMessage,
} from './order-fiscal-result';

const CANCEL_FAILED_MESSAGE = 'Отменить платёж не получилось. Попробуйте ещё раз или выберите вариант ниже.';

const CANCEL_WITHOUT_TERMINAL_ID_MESSAGE =
  'Отменить отсюда нельзя: терминал не сохранил данные оплаты. Если деньги списались, отмените операцию на терминале и отметьте платёж ссылкой ниже.';

/** Неясный платёж по карте: терминал не дал понятного ответа, оплату закрывает отмена. */
interface PaymentReview {
  execution: OrderFiscalExecutionFieldsFragment;
  technicalDetails: string | null;
}

interface FiscalPaymentReviewDependencies {
  adapter: Pick<KkmServerAdapter, 'execute' | 'getResult'>;
  services: Pick<OrderFiscalizationServices, 'find' | 'cancelPayment' | 'refreshOrder'>;
  storage: Pick<FiscalWorkstationStorage, 'clearPendingOperation'>;
  handshakeBoundKkt: (deviceId: string | null | undefined) => ReturnType<KkmServerAdapter['handshake']>;
  setRetryableExecution: (execution: OrderFiscalExecutionFieldsFragment | null) => void;
  transition: (
    phase: OrderFiscalizationPhase,
    message?: string | null,
    execution?: OrderFiscalExecutionFieldsFragment,
  ) => void;
}

/** Проверка неопределённого списания и отмена: результат банка не теряется между чтениями. */
export class FiscalPaymentReview {
  public review: PaymentReview | null = null;
  public cashToReturn = 0;
  public cancelledOrderStatus: OrderStatus | null = null;

  public constructor(private readonly dependencies: FiscalPaymentReviewDependencies) {}

  public async cancelUnconfirmedPayment(
    origin: OrderFiscalExecutionFieldsFragment,
    captured: boolean,
  ): Promise<OrderFieldsFragment | null> {
    try {
      this.dependencies.transition('cancelling_payment', null, origin);
      const execution = await this.dependencies.services.find(origin.operation.order_id);
      if (execution === null) return await this.refreshReviewedOrder(origin.operation.order_id);
      if (
        execution.operation.id !== origin.operation.id ||
        (captured
          ? !isReceiptRetryAllowed(execution)
          : execution.next_action !== OrderFiscalRecoveryAction.CancelPayment)
      ) {
        this.review = null;
        this.dependencies.setRetryableExecution(null);
        this.dependencies.transition(
          'print_result_unknown',
          'Состояние оплаты изменилось. Откройте запись заново.',
          execution,
        );
        return null;
      }

      const terminalId = execution.execution_target.terminal_device_id;
      const cardAmount = execution.receipt_plan.payments.find((payment) => payment.use_acquiring)?.amount;
      if (terminalId == null || cardAmount === undefined) throw new Error('Для отмены не найден привязанный терминал.');
      const ready = requireReadyHandshake(
        await this.dependencies.handshakeBoundKkt(execution.execution_target.kkt_device_id),
      );
      assertExecutionTarget(execution, ready);
      if (!ready.terminals.some((snapshot) => snapshot.device.idDevice === terminalId)) {
        throw new Error('Подключён другой терминал.');
      }

      const deviceKey = `terminal:${terminalId}`;
      // Принятую оплату терминал находит по её идентификатору; у неясной он есть,
      // только если терминал всё же сохранил результат исходной команды.
      const universalId = captured
        ? (execution.terminal_transaction_id ?? null)
        : ((await this.recheckPaymentResult(execution, null, deviceKey))?.acquiring?.universalId ?? null);
      if (captured && universalId === null) throw new Error('У принятой оплаты нет идентификатора терминала.');
      // Без UniversalID отмена ушла бы на терминал без RRN: какую операцию он отменит, решает ПО банка.
      if (universalId === null) {
        console.warn('[OrderFiscalizationController.payment-cancel-without-terminal-id]', {
          operationId: execution.operation.id,
        });
        this.showUnconfirmedPayment(
          execution,
          this.review?.technicalDetails ?? null,
          CANCEL_WITHOUT_TERMINAL_ID_MESSAGE,
        );
        return null;
      }

      this.dependencies.transition('cancelling_payment', null, execution);
      const result = await executeKkmSafely(() =>
        this.dependencies.adapter.execute(
          mapCardCancelCommand({
            amount: cardAmount,
            // Отмена повторяема: у каждой попытки свой IdCommand, иначе KKMServer ответит кэшем.
            idCommand: crypto.randomUUID(),
            terminalDeviceId: terminalId,
            universalId,
          }),
          { deviceKey, timeoutClass: 'acquiring' },
        ),
      );
      const outcome = classifyCardCancel(result);

      // Принятую оплату закрывает только выполненная отмена: «отменять нечего» ей противоречит.
      if (outcome === 'failed' || (captured && outcome !== 'reversed')) {
        this.showCancelFailure(execution, result.kind === 'error' ? result.error.vendorMessage : null);
        return null;
      }

      return await this.closeCancelledPayment(
        execution,
        {
          bank: result.acquiring === null ? undefined : toBankEvidenceInput(result.acquiring),
          failureKind:
            outcome === 'reversed'
              ? OrderFiscalFailureKind.PaymentReversed
              : OrderFiscalFailureKind.CancelledNotCharged,
        },
        outcome === 'reversed' ? 'Деньги возвращены клиенту' : 'Списания не было',
      );
    } catch (error) {
      this.showCancelFailure(origin, safeErrorMessage(error, CANCEL_FAILED_MESSAGE));
      return null;
    }
  }

  public async cancelWithoutCheck(execution: OrderFiscalExecutionFieldsFragment): Promise<OrderFieldsFragment | null> {
    try {
      return await this.closeCancelledPayment(
        execution,
        { failureKind: OrderFiscalFailureKind.CancelledUnverified },
        'Платёж отменён без проверки',
      );
    } catch (error) {
      this.showCancelFailure(execution, safeErrorMessage(error, CANCEL_FAILED_MESSAGE));
      return null;
    }
  }

  private async closeCancelledPayment(
    execution: OrderFiscalExecutionFieldsFragment,
    input: Omit<CancelOrderFiscalPaymentInput, 'operationId'>,
    message: string,
  ): Promise<OrderFieldsFragment> {
    const cancelled = await this.dependencies.services.cancelPayment({ ...input, operationId: execution.operation.id });
    this.cashToReturn = cancelled.cash_amount;
    this.dependencies.storage.clearPendingOperation(execution.operation.id);
    const order = await this.dependencies.services.refreshOrder(execution.operation.order_id);
    this.review = null;
    this.dependencies.setRetryableExecution(null);
    this.cancelledOrderStatus = order.status;
    this.dependencies.transition('payment_cancelled', message, execution);
    return order;
  }

  /** Экран остаётся открытым: операция на сервере не менялась, отмену можно повторить. */
  private showCancelFailure(execution: OrderFiscalExecutionFieldsFragment, technicalDetails: string | null): void {
    console.warn('[OrderFiscalizationController.payment-cancel-failed]', { operationId: execution.operation.id });

    if (this.review === null) {
      this.dependencies.setRetryableExecution(execution);
      this.dependencies.transition(
        'payment_without_receipt',
        'Отменить платёж не получилось. Попробуйте ещё раз или повторите печать чека.',
        execution,
      );
      return;
    }

    this.showUnconfirmedPayment(execution, technicalDetails ?? this.review.technicalDetails, CANCEL_FAILED_MESSAGE);
  }

  public showUnconfirmedPayment(
    execution: OrderFiscalExecutionFieldsFragment,
    technicalDetails: string | null,
    message: string | null = null,
  ): void {
    this.review = { execution, technicalDetails };
    this.dependencies.transition('payment_unconfirmed', message, execution);
  }

  public async refreshReviewedOrder(orderId: string): Promise<OrderFieldsFragment | null> {
    const order = await this.dependencies.services.refreshOrder(orderId);
    if (this.review !== null) this.dependencies.storage.clearPendingOperation(this.review.execution.operation.id);
    this.review = null;
    this.dependencies.setRetryableExecution(null);
    if (order.status === OrderStatus.Cancelled) {
      this.dependencies.transition('order_cancelled', 'Оплата отменена. Можно закрыть этот экран.');
      return null;
    }
    if (order.status === OrderStatus.Draft) {
      this.dependencies.transition('failed_before_print', 'Предыдущая попытка закрыта. Можно вернуться к оплате.');
      return null;
    }
    this.dependencies.transition('completed');
    return order;
  }

  public async recheckPaymentResult(
    execution: OrderFiscalExecutionFieldsFragment,
    original: KkmExecutionResult | null,
    deviceKey: string,
  ): Promise<KkmExecutionResult | null> {
    this.dependencies.transition(
      'checking_payment_result',
      original?.kind === 'error' && original.error.vendorCode === '4119'
        ? 'Терминал сообщил об ошибке связи с банком. Уточняем, прошла ли оплата.'
        : 'Уточняем результат у терминала. Дождитесь завершения проверки.',
      execution,
    );
    const idCommand = acquiringCommandId(execution.id_command);
    let checked: KkmExecutionResult;

    try {
      checked = await this.dependencies.adapter.getResult(idCommand, { deviceKey, timeoutClass: 'acquiring' });
      // Перепроверка идёт мимо executeKkmSafely: уведомление устройства показываем здесь.
      notifyKkmVendorNotice(checked);
    } catch {
      // Сбой чтения не меняет исход оплаты; причина и обмен остаются в журнале KKMServer.
      console.warn('[OrderFiscalizationController.payment-result-check-failed]', {
        operationId: execution.operation.id,
      });
      return original;
    }

    if (checked.response.command !== 'PayByPaymentCard' || checked.response.idCommand !== idCommand) {
      return original;
    }

    const conflictingEvidence = BANK_REFERENCE_FIELDS.some((field) => {
      const previous = original?.acquiring?.[field];
      const current = checked.acquiring?.[field];
      return previous && current && previous !== current;
    });
    if (conflictingEvidence) return original;

    if (checked.kind === 'ok' && checked.acquiring !== null && (checked.acquiring.rrn || original?.acquiring?.rrn)) {
      // GetRezult может вернуть меньше реквизитов, чем первоначальный ответ.
      // Сохраняем уже известный идентификатор платежа для последующего возврата.
      return { ...checked, acquiring: mergeAcquiring(checked.acquiring, original?.acquiring) };
    }

    // NotFound и сбой самого чтения не доказывают отсутствие списания. Разрешаем
    // повтор только по подтверждённому отказу исходной банковской команды, без
    // свидетельств списания в обоих ответах.
    const hasBankEvidence = hasBankReferences(original) || hasBankReferences(checked);
    if (
      original?.kind !== 'ok' &&
      !hasBankEvidence &&
      checked.kind === 'error' &&
      (checked.error.kind === 'payment_declined' || checked.error.kind === 'user_cancelled')
    ) {
      return checked;
    }

    // Даже неполное свидетельство списания нельзя потерять между повторными чтениями.
    if (original?.kind === 'ok' && original.acquiring?.rrn) return original;
    if (hasBankEvidence) {
      const source = original ?? checked;
      const error = source.kind === 'error' ? source.error : null;
      return {
        ...source,
        kind: 'error',
        error: {
          kind: 'device_error',
          outcome: 'unknown',
          message: 'Результат оплаты не подтверждён.',
          vendorCode: error?.vendorCode ?? null,
          vendorMessage: error?.vendorMessage ?? null,
        },
        acquiring: mergeAcquiring(original?.acquiring, checked.acquiring),
      };
    }
    return original ?? checked;
  }
}
